import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { NewTenantDialog } from "@/components/NewTenantDialog.tsx";
import { api } from "@/lib/api.ts";
import {
  type MetricsOut,
  STATUS_LABELS,
  STATUS_TONES,
  type TenantOut,
  ago,
  formatPEN,
} from "@/lib/types.ts";

export function DashboardPage() {
  const metrics = useQuery({
    queryKey: ["metrics"],
    queryFn: () => api.get<MetricsOut>("/metrics"),
    refetchInterval: 30_000,
  });
  const tenants = useQuery({
    queryKey: ["tenants"],
    queryFn: () => api.get<TenantOut[]>("/tenants"),
    refetchInterval: 30_000,
  });
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6">
      {metrics.data && <MetricsRow m={metrics.data} />}

      <section className="rounded-[1.375rem] border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="font-extrabold text-slate-900">Empresas</h2>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="flex items-center gap-1 rounded-xl bg-brand-600 px-4 py-2 text-sm font-bold text-white hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" /> Nueva empresa
          </button>
        </div>
        {tenants.isError && <p className="p-6 text-sm text-red-700">No se pudo cargar.</p>}
        {tenants.data && tenants.data.length === 0 && (
          <p className="p-6 text-center text-sm text-slate-500">Todavía no hay empresas.</p>
        )}
        {tenants.data && tenants.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-[#f8fafa] text-[11px] font-bold tracking-wider text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-2 text-left">Empresa</th>
                <th className="px-4 py-2 text-left">Estado</th>
                <th className="px-4 py-2 text-right">Ventas hoy</th>
                <th className="px-4 py-2 text-left">Última venta</th>
                <th className="px-4 py-2 text-left">Servers</th>
                <th className="px-4 py-2 text-left">SUNAT</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tenants.data.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/tenants/${t.id}`} className="font-bold text-slate-900 hover:text-brand-700">
                      {t.trade_name || t.legal_name}
                    </Link>
                    <p className="text-xs text-slate-500">
                      RUC {t.ruc} · {t.plan} · {t.branches} sucursal(es)
                    </p>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_TONES[t.status]}`}>
                      {STATUS_LABELS[t.status]}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatPEN(t.sales_today_total)}
                    <span className="ml-1 text-xs text-slate-400">({t.sales_today})</span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{ago(t.last_sale_at)}</td>
                  <td className="px-4 py-2 text-xs">
                    {t.nodes === 0 ? (
                      <span className="text-slate-400">online</span>
                    ) : (
                      <span className={t.nodes_down || t.nodes_backup_stale ? "text-amber-700" : "text-slate-600"}>
                        {t.nodes} · {t.nodes_down} sin contacto · {t.nodes_backup_stale} sin backup
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {t.billing_rejected > 0 ? (
                      <span className="text-red-700">{t.billing_rejected} rechazados</span>
                    ) : t.billing_pending > 0 ? (
                      <span className="text-sky-700">{t.billing_pending} pendientes</span>
                    ) : (
                      <span className="text-slate-400">al día</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {creating && <NewTenantDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

function MetricsRow({ m }: { m: MetricsOut }) {
  const active = (m.tenants_by_status.active ?? 0) + (m.tenants_by_status.trial ?? 0);
  const alerts = m.nodes_down + m.nodes_backup_stale + m.billing_rejected;
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <Card label="Empresas operativas" value={String(active)} />
      <Card label="Ventas de hoy" value={formatPEN(m.sales_today_total)} hint={`${m.sales_today} ventas`} />
      <Card label="Servers locales" value={String(m.nodes)} hint={`${m.nodes_down} sin contacto`} />
      <Card
        label="Alertas"
        value={String(alerts)}
        hint={`${m.nodes_backup_stale} backups vencidos · ${m.billing_rejected} rechazos SUNAT`}
        warn={alerts > 0}
      />
    </div>
  );
}

function Card({ label, value, hint, warn }: { label: string; value: string; hint?: string; warn?: boolean }) {
  return (
    <div className="rounded-[1.375rem] border border-slate-200 bg-white p-4">
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      <p className={`mt-1 flex items-center gap-1 text-xl font-semibold ${warn ? "text-amber-700" : "text-slate-900"}`}>
        {warn && <AlertTriangle className="h-4 w-4" />}
        {value}
      </p>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}
